import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AktionenPage() {
  const [aktionen, setAktionen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAktion, setSelectedAktion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchAktionen();
  }, []);

  const fetchAktionen = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/aktionen?nur_aktive=true`);
      if (response.ok) {
        const data = await response.json();
        setAktionen(data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Aktionen:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const openDetails = (aktion) => {
    setSelectedAktion(aktion);
    setDialogOpen(true);
  };

  // Berechne Größe basierend auf Priorität und Anzahl
  const getGridClass = (index, total, prioritaet) => {
    if (total === 1) return 'col-span-full';
    if (prioritaet >= 10) return 'col-span-full md:col-span-2';
    if (prioritaet >= 5) return 'col-span-full md:col-span-1';
    if (total <= 3) return 'col-span-full md:col-span-1';
    return 'col-span-1';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#EFDF00] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#EFDF00] hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück zur Startseite</span>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Aktuelle <span className="text-[#EFDF00]">Aktionen</span>
          </h1>
          <p className="text-xl text-gray-300">
            Entdecken Sie unsere aktuellen Angebote und Sonderaktionen
          </p>
        </div>
      </section>

      {/* Aktionen Grid */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        {aktionen.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-600 mb-2">Keine aktiven Aktionen</h2>
            <p className="text-gray-500">Schauen Sie bald wieder vorbei für neue Angebote!</p>
            <Link to="/">
              <Button className="mt-6 bg-[#EFDF00] text-black hover:bg-yellow-400">
                Zur Startseite
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aktionen.map((aktion, index) => (
              <div
                key={aktion.id}
                className={`bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-xl ${getGridClass(index, aktionen.length, aktion.prioritaet)}`}
                onClick={() => openDetails(aktion)}
                data-testid={`aktion-card-${aktion.id}`}
              >
                {/* Bild */}
                <div className="relative h-48 md:h-56 bg-gradient-to-br from-[#EFDF00] to-yellow-500">
                  {aktion.bild_url ? (
                    <img
                      src={aktion.bild_url}
                      alt={aktion.titel}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Tag className="w-20 h-20 text-black/20" />
                    </div>
                  )}
                  {/* Prioritäts-Badge */}
                  {aktion.prioritaet >= 5 && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      TOP ANGEBOT
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-black mb-2 line-clamp-2">
                    {aktion.titel}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {aktion.kurzbeschreibung || aktion.beschreibung}
                  </p>

                  {/* Zeitraum */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {formatDate(aktion.start_datum)} - {formatDate(aktion.end_datum)}
                    </span>
                  </div>

                  {/* Button */}
                  <div className="flex items-center justify-between">
                    <span className="text-[#EFDF00] font-semibold flex items-center gap-1">
                      Details ansehen
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedAktion && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">
                  {selectedAktion.titel}
                </DialogTitle>
              </DialogHeader>

              {/* Bild */}
              {selectedAktion.bild_url && (
                <div className="w-full h-64 rounded-lg overflow-hidden mb-4">
                  <img
                    src={selectedAktion.bild_url}
                    alt={selectedAktion.titel}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Zeitraum */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#EFDF00]" />
                  <div>
                    <p className="text-sm text-gray-500">Gültig von</p>
                    <p className="font-semibold">{formatDate(selectedAktion.start_datum)}</p>
                  </div>
                </div>
                <div className="w-px h-10 bg-gray-300" />
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#EFDF00]" />
                  <div>
                    <p className="text-sm text-gray-500">Gültig bis</p>
                    <p className="font-semibold">{formatDate(selectedAktion.end_datum)}</p>
                  </div>
                </div>
              </div>

              {/* Beschreibung */}
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedAktion.beschreibung}
                </p>
              </div>

              {/* CTA */}
              <div className="mt-6 flex gap-4">
                <Link to="/termin" className="flex-1">
                  <Button className="w-full bg-[#EFDF00] text-black hover:bg-yellow-400 py-6">
                    Termin vereinbaren
                  </Button>
                </Link>
                <Link to="/kontakt" className="flex-1">
                  <Button variant="outline" className="w-full border-black py-6">
                    Kontakt aufnehmen
                  </Button>
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
